import React, { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { cateringPackages } from '../../content/siteContent.mjs';
import publicService from '../../services/publicService';
import { Button } from '../ui/button';

const PACKAGE_OPTIONS = ['Not sure yet', ...cateringPackages.map((p) => p.name)];

const FIELDS = {
  wholesale: [
    { name: 'name', label: 'Your name', type: 'text', required: true, half: true },
    { name: 'business_name', label: 'Business name', type: 'text', required: true, half: true },
    { name: 'email', label: 'Email', type: 'email', required: true, half: true },
    { name: 'phone', label: 'Phone (optional)', type: 'tel', half: true },
    { name: 'town', label: 'Town', type: 'text', required: true, half: true, placeholder: 'e.g. Pleasantville, NY' },
    { name: 'volume', label: 'Rough daily volume', type: 'text', half: true, placeholder: 'e.g. 3 dozen a day' },
    { name: 'message', label: 'Anything else we should know?', type: 'textarea', placeholder: 'Opening time, flavours your customers ask for, delivery mornings you need…' },
  ],
  catering: [
    { name: 'name', label: 'Your name', type: 'text', required: true, half: true },
    { name: 'email', label: 'Email', type: 'email', required: true, half: true },
    { name: 'phone', label: 'Phone (optional)', type: 'tel', half: true },
    { name: 'event_date', label: 'Date needed', type: 'date', half: true },
    { name: 'package', label: 'Package', type: 'select', options: PACKAGE_OPTIONS, half: true },
    { name: 'guests', label: 'Guests / donuts needed', type: 'text', half: true, placeholder: 'e.g. 30 people' },
    { name: 'town', label: 'Delivery town', type: 'text', half: true, placeholder: 'e.g. White Plains, NY' },
    { name: 'message', label: 'Tell us about the event', type: 'textarea', placeholder: 'Delivery time, flavour preferences, vegan guests…' },
  ],
  general: [
    { name: 'name', label: 'Your name', type: 'text', required: true, half: true },
    { name: 'email', label: 'Email', type: 'email', required: true, half: true },
    { name: 'phone', label: 'Phone (optional)', type: 'tel', half: true },
    { name: 'business_name', label: 'Business (optional)', type: 'text', half: true },
    { name: 'message', label: 'Your message', type: 'textarea', required: true },
  ],
};

const SUBMIT_LABEL = {
  wholesale: 'Send wholesale inquiry',
  catering: 'Request catering quote',
  general: 'Send message',
};

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-base text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-200 transition-colors';

/** Lead-capture form for the public site — replaces the old mailto: links. */
export const InquiryForm = ({ variant = 'general', initialValues = {}, testId = 'inquiry-form' }) => {
  const fields = FIELDS[variant];
  const [values, setValues] = useState(() =>
    fields.reduce((acc, f) => ({ ...acc, [f.name]: initialValues[f.name] || '' }), { company: '' })
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (name) => (event) => setValues((prev) => ({ ...prev, [name]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const response = await publicService.submitInquiry({
        type: variant,
        source_page: window.location.pathname,
        ...values,
      });
      setDone(response.message);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not send that — please email info@maddonutshop.com.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center"
        data-testid={`${testId}-success`}
      >
        <CheckCircle2 size={40} className="mx-auto mb-4 text-amber-600" aria-hidden="true" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Inquiry sent</h3>
        <p className="text-base text-gray-600">{done}</p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-lg"
      data-testid={testId}
      noValidate={false}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {fields.map((field) => (
          <div key={field.name} className={field.half ? '' : 'sm:col-span-2'}>
            <label htmlFor={`${testId}-${field.name}`} className="mb-1.5 block text-sm font-medium text-gray-700">
              {field.label}
              {field.required && <span className="ml-1 text-amber-600">*</span>}
            </label>
            {field.type === 'textarea' ? (
              <textarea
                id={`${testId}-${field.name}`}
                name={field.name}
                rows={4}
                required={field.required}
                placeholder={field.placeholder}
                value={values[field.name]}
                onChange={set(field.name)}
                className={inputClass}
                data-testid={`${testId}-${field.name}`}
              />
            ) : field.type === 'select' ? (
              <select
                id={`${testId}-${field.name}`}
                name={field.name}
                value={values[field.name]}
                onChange={set(field.name)}
                className={inputClass}
                data-testid={`${testId}-${field.name}`}
              >
                <option value="">Choose a package</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`${testId}-${field.name}`}
                name={field.name}
                type={field.type}
                required={field.required}
                placeholder={field.placeholder}
                value={values[field.name]}
                onChange={set(field.name)}
                className={inputClass}
                data-testid={`${testId}-${field.name}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Honeypot: bots fill it, humans never see it. */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor={`${testId}-company`}>Company</label>
        <input
          id={`${testId}-company`}
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.company}
          onChange={set('company')}
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          type="submit"
          size="lg"
          disabled={submitting}
          className="bg-gray-900 text-white hover:bg-gray-800 px-8"
          data-testid={`${testId}-submit`}
        >
          {submitting && <Loader2 size={18} className="mr-2 animate-spin" />}
          {submitting ? 'Sending…' : SUBMIT_LABEL[variant]}
        </Button>
        <p className="text-sm text-gray-500">We reply within one business day. No newsletters, ever.</p>
      </div>
    </form>
  );
};

export default InquiryForm;
